import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Download, Loader2, MapPinIcon, PyramidIcon, WrenchIcon } from "lucide-react";
import { SiGithub, SiLinkedin } from "react-icons/si";
import emailjs from "@emailjs/browser";
import AboutCard from "./AboutCard";
import { useLang } from "../i18n/LangContext";

export default function About() {
  const { t } = useLang();
  const [message, setMessage] = useState(null);
  const [sending, setSending] = useState(false);
  const form = useRef();

  const projects = [
    {
      id: 1,
      title: "Corec",
      description: t("projects.corec"),
      tags: ["Python", "CARS", "Open-source"],
      url: "https://github.com/jgimillo/corec",
    },
    {
      id: 2,
      title: "Smart Assure",
      description: t("projects.smartAssure"),
      tags: ["LangChain", "Databricks", "MLflow"],
      url: "https://www.agenttravel.es/noticia-057780_Intermundial-transforma-la-gestion-de-siniestros-con-un-sistema-automatizado-y....html",
    },
    {
      id: 3,
      title: "Ad Astra",
      description: t("projects.adAstra"),
      tags: ["C", "Videogame", "Academic"],
      url: "https://github.com/EduardoTerres/Ad-Astra-C-videogame",
    },
    {
      id: 4,
      title: "Chat Babel",
      description: t("projects.chatBabel"),
      tags: ["AWS", "Vue", "Erasmus+"],
      url: "https://github.com/niegrzybkowski/chat-babel",
    }
  ];

  const sendEmail = (e) => {
    e.preventDefault();
    setSending(true);

    emailjs
      .sendForm(
        "service_rgk4c1z",
        "template_4l3rjtd",
        form.current,
        "ozvYYG5nG0LbpXdFD"
      )
      .then(
        () => {
          setMessage({ text: t("contact.success"), type: "success" });
          form.current.reset();
          setTimeout(() => setMessage(null), 3000);
        },
        (error) => {
          setMessage({ text: t("contact.error") + error.text, type: "error" });
          setTimeout(() => setMessage(null), 5000);
        }
      )
      .finally(() => setSending(false));
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* SALUDO Y CONTACTO RÁPIDO */}
      <section className="mt-2 md:mt-8 grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
        <div className="md:col-span-2">
          <AboutCard />

          {/* PROYECTOS */}
          <section id="projects" className="mt-10">
            <h3 className="text-2xl font-semibold mb-4">{t("projects.title")}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {projects.map((p) => (
                <motion.a
                  key={p.id}
                  href={p.url}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: p.id * 0.08 }}
                  className="block rounded-xl p-5 bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-800 shadow-sm hover:shadow-md transition-shadow"
                >
                  <h4 className="text-lg font-medium">{p.title}</h4>
                  <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">{p.description}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {p.tags.map((t) => (
                      <span key={t} className="text-xs ring-1 ring-slate-200 dark:ring-slate-700 px-2 py-1 rounded-full">
                        {t}
                      </span>
                    ))}
                  </div>
                </motion.a>
              ))}
            </div>
          </section>
        </div>

        {/* ASIDE CONTACTO */}
        <aside className="hidden md:block">
          <div className="sticky top-8 rounded-xl p-6 bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800 shadow-sm">
            <h4 className="font-semibold mb-3">{t("contactAside.title")}</h4>
            <div className="mt-4 flex flex-col gap-2">
              <p className="text-sm inline-flex items-center gap-2">
                <MapPinIcon size={14} /> {t("contactAside.location")}
              </p>
              <a href="#contact" className="text-sm inline-flex items-center gap-2">
                <Mail size={14} /> jaimegimillo@gmail.com
              </a>
              <a href="https://www.linkedin.com/in/jaime-gimillo" className="text-sm inline-flex items-center gap-2">
                <SiLinkedin size={14} /> linkedin.com/in/jaime-gimillo
              </a>
              <a href="https://github.com/jgimillo" className="text-sm inline-flex items-center gap-2">
                <SiGithub size={14} /> github.com/jgimillo
              </a>
            </div>
          </div>
        </aside>
      </section>

      {/* SOBRE MÍ */}
      <section className="mt-12 rounded-2xl p-5 sm:p-8 bg-slate-50 dark:bg-slate-900 shadow-md">
        <h3 className="text-2xl font-semibold mb-3">{t("aboutMe.title")}</h3>
        <p className="text-slate-600 dark:text-slate-300 mb-3">
          {t("aboutMe.p1")}
        </p>
        <p className="text-slate-600 dark:text-slate-300 mb-3">
          {t("aboutMe.p2")}
        </p>
        <p className="text-slate-600 dark:text-slate-300 mb-3">
          {t("aboutMe.p3")}
        </p>
        <ul className="list-none space-y-1 mb-3">
          <li className="flex items-center gap-2">
            <PyramidIcon 
              size={17} 
              className="flex-shrink-0 text-slate-500 dark:text-slate-400" 
            />
            <a
              href="TFG_MAT_Jaime_Gimillo.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-600 hover:text-slate-700 dark:text-slate-300 hover:underline"
            >
              {t("aboutMe.tfgMath")}
            </a>
          </li>
          <li className="flex items-center gap-2">
            <WrenchIcon 
              size={17} 
              className="flex-shrink-0 text-slate-500 dark:text-slate-400" 
            />
            <a
              href="TFG_INFO_Jaime_Gimillo.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-600 hover:text-slate-700 dark:text-slate-300 hover:underline"
            >
              {t("aboutMe.tfgInfo")}
            </a>
          </li>
        </ul>
        <hr className="my-5 border-slate-300 dark:border-slate-700"/>
        <p className="text-slate-600 dark:text-slate-300 mb-3">
          {t("aboutMe.p4")}
        </p>

        <p className="text-slate-600 dark:text-slate-300">
          {t("aboutMe.p5")}
        </p>

        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Stat label={t("aboutMe.years")} value="2+" />
          <Stat label={t("aboutMe.projects")} value="4+" />
        </div>

        <div className="mt-6 flex flex-wrap gap-3">
          <a
            href="CV_Jaime_Gimillo.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900 text-white dark:bg-white dark:text-slate-900 hover:shadow-md"
          >
            <Download size={16}/> CV
          </a>
        </div>
      </section>

      {/* CONTACTO */}
      <section id="contact" className="mt-12 mb-12 md:mb-16 rounded-2xl p-5 sm:p-8 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 shadow-sm">
        <h3 className="text-2xl font-semibold mb-3">{t("contact.title")}</h3>
        <p className="text-slate-600 dark:text-slate-300 mb-6">{t("contact.subtitle")}</p>

        <form ref={form} onSubmit={sendEmail} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input
            name="user_name"
            className="col-span-1 md:col-span-1 rounded-md px-4 py-3 ring-1 ring-slate-200 dark:ring-slate-800 bg-transparent"
            placeholder={t("contact.name")}
            type="text"
            required
          />
          <input
            name="user_email"
            className="col-span-1 md:col-span-1 rounded-md px-4 py-3 ring-1 ring-slate-200 dark:ring-slate-800 bg-transparent"
            placeholder={t("contact.email")}
            type="email"
            required
          />
          <textarea
            name="message"
            className="col-span-1 md:col-span-2 rounded-md px-4 py-3 ring-1 ring-slate-200 dark:ring-slate-800 bg-transparent h-32"
            placeholder={t("contact.message")}
            required
          />

          <div className="col-span-1 md:col-span-2 flex items-center gap-3">
            <button
              type="submit"
              disabled={sending}
              className="min-w-[75px] min-h-[40px] px-4 py-2 rounded-md bg-slate-900 text-white dark:bg-white dark:text-slate-900 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed hover:shadow-md"
            >
              {sending ? <Loader2 className="animate-spin h-6 w-6" /> : t("contact.send")}
            </button>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {t("contact.orMail")} <a href="mailto:jaimegimillo@gmail.com" className="underline">jaimegimillo@gmail.com</a>
            </p>
          </div>
        </form>
      </section>

      {/* NOTIFICACIONES */}
      <AnimatePresence>
        {message && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            transition={{ duration: 0.3 }}
            className={`fixed bottom-6 right-6 px-4 py-3 rounded-md shadow-lg text-white z-50 ${
              message.type === "success" ? "bg-green-500" : "bg-red-500"
            }`}
          >
            {message.text}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// COMPONENTE AUXILIAR
function Stat({ label, value }) {
  return (
    <div className="p-4 bg-white dark:bg-slate-800 rounded-md border border-slate-100 dark:border-slate-800">
      <div className="text-sm text-slate-500 dark:text-slate-400">{label}</div>
      <div className="text-xl font-semibold">{value}</div>
    </div>
  );
}
